/**
 * Original, generated product "packshots" used until a store uploads real photography. Each category gets its own
 * packaging silhouette (longneck, can, square whisky bottle, wine bottle, PET water bottle, chip bag, ice bag …) and
 * the label carries the product's brand and size, coloured deterministically from the brand name. No third-party
 * artwork or trademarks are reproduced — brand names appear only as plain text on a generic label.
 */
import { useId, type ReactElement } from 'react';

const PALETTES: [string, string, string][] = [
  // [label, label text, accent]
  ['#0F5E64', '#FFFFFF', '#F2A541'],
  ['#B83535', '#FFFFFF', '#F9D7A6'],
  ['#121821', '#F2A541', '#FFFFFF'],
  ['#2459B8', '#FFFFFF', '#A8D7DA'],
  ['#1F9D55', '#FFFFFF', '#FDEED8'],
  ['#7C2341', '#FDEED8', '#F2A541'],
  ['#F2A541', '#121821', '#B46A0C'],
  ['#6D3F8F', '#FFFFFF', '#F9D7A6'],
  ['#FFFFFF', '#0B4A4F', '#D64545'],
];

function hash(s: string) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return Math.abs(h);
}

type Shape = 'longneck' | 'can' | 'square' | 'tall' | 'wine' | 'round' | 'pet' | 'soda' | 'bag' | 'ice' | 'box';

function shapeFor(category: string, volumeMl: number | null): Shape {
  switch (category) {
    case 'beer':
      return volumeMl !== null && volumeMl <= 500 ? 'can' : 'longneck';
    case 'rtd':
    case 'energy-drinks':
      return 'can';
    case 'whisky':
    case 'brandy':
    case 'rum':
      return 'square';
    case 'vodka':
    case 'gin':
      return 'tall';
    case 'wine':
      return 'wine';
    case 'tequila':
    case 'liqueur':
      return 'round';
    case 'water':
      return 'pet';
    case 'soda':
    case 'tonic':
    case 'mixers':
    case 'soft-drinks':
    case 'juices':
    case 'non-alcoholic':
      return volumeMl !== null && volumeMl <= 330 ? 'can' : 'soda';
    case 'snacks':
    case 'namkeen':
    case 'nuts':
      return 'bag';
    case 'ice':
      return 'ice';
    default:
      return 'box';
  }
}

const LIQUID: Record<string, string> = {
  beer: '#7A4A0E',
  whisky: '#B4650F',
  brandy: '#8A4B19',
  rum: '#5E2C0E',
  vodka: '#DCE9F2',
  gin: '#CFEDEA',
  wine: '#3B0D1C',
  tequila: '#E6C35C',
  liqueur: '#4A2A5E',
  water: '#CFE3FF',
  soda: '#E8F7F6',
  tonic: '#EAF8EE',
  'soft-drinks': '#3A1A0E',
  juices: '#F2A541',
};

const volumeLabel = (ml: number | null) => (ml === null ? '' : ml >= 1000 ? `${(ml / 1000).toFixed(ml % 1000 ? 1 : 0)} L` : `${ml} ml`);

export function Packshot({ brand, name, categorySlug, volumeMl, className = '' }: { brand: string; name: string; categorySlug: string; volumeMl: number | null; className?: string }) {
  const uid = useId().replace(/:/g, '');
  const [label, labelText, accent] = PALETTES[hash(brand || name) % PALETTES.length]!;
  const shape = shapeFor(categorySlug, volumeMl);
  const liquid = LIQUID[categorySlug] ?? '#5B6573';
  const brandText = (brand || name).toUpperCase().slice(0, 14);
  const vol = volumeLabel(volumeMl);
  const glass = `url(#g${uid})`;
  const shine = `url(#s${uid})`;

  const Label = ({ x, y, w, h, rx = 3 }: { x: number; y: number; w: number; h: number; rx?: number }) => (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={rx} fill={label} stroke={accent} strokeWidth="1.2" />
      <text x={x + w / 2} y={y + h * 0.48} textAnchor="middle" fontFamily="Inter Variable, Inter, sans-serif" fontWeight="800" fontSize={Math.min(9, (w * 1.55) / Math.max(4, brandText.length))} fill={labelText} letterSpacing="0.3">
        {brandText}
      </text>
      {vol && (
        <text x={x + w / 2} y={y + h * 0.8} textAnchor="middle" fontFamily="Inter Variable, Inter, sans-serif" fontWeight="600" fontSize="6.5" fill={labelText} opacity="0.85">
          {vol}
        </text>
      )}
    </g>
  );

  let body: ReactElement;
  switch (shape) {
    case 'longneck':
      body = (
        <g>
          <rect x="53" y="10" width="14" height="8" rx="2" fill={accent} />
          <path d="M54 18 H66 V40 Q78 50 78 64 V106 Q78 112 72 112 H48 Q42 112 42 106 V64 Q42 50 54 40 Z" fill={liquid} />
          <path d="M54 18 H66 V40 Q78 50 78 64 V106 Q78 112 72 112 H48 Q42 112 42 106 V64 Q42 50 54 40 Z" fill={shine} />
          <Label x={44} y={68} w={32} h={26} />
        </g>
      );
      break;
    case 'can':
      body = (
        <g>
          <rect x="40" y="22" width="40" height="90" rx="7" fill={label} />
          <rect x="42" y="18" width="36" height="7" rx="3" fill="#A9B1BC" />
          <rect x="40" y="22" width="40" height="90" rx="7" fill={shine} />
          <rect x="40" y="44" width="40" height="3" fill={accent} />
          <rect x="40" y="88" width="40" height="3" fill={accent} />
          <text x="60" y="66" textAnchor="middle" fontFamily="Inter Variable, Inter, sans-serif" fontWeight="800" fontSize={Math.min(9, 60 / Math.max(4, brandText.length))} fill={labelText}>
            {brandText}
          </text>
          <text x="60" y="80" textAnchor="middle" fontFamily="Inter Variable, Inter, sans-serif" fontWeight="600" fontSize="6.5" fill={labelText} opacity="0.85">
            {vol}
          </text>
        </g>
      );
      break;
    case 'square':
      body = (
        <g>
          <rect x="52" y="8" width="16" height="12" rx="2" fill={accent} />
          <path d="M54 20 H66 V30 Q82 32 82 44 V108 Q82 112 78 112 H42 Q38 112 38 108 V44 Q38 32 54 30 Z" fill={glass} />
          <path d="M40 58 H80 V108 Q80 110 78 110 H42 Q40 110 40 108 Z" fill={liquid} opacity="0.92" />
          <path d="M54 20 H66 V30 Q82 32 82 44 V108 Q82 112 78 112 H42 Q38 112 38 108 V44 Q38 32 54 30 Z" fill={shine} />
          <Label x={42} y={60} w={36} h={30} />
        </g>
      );
      break;
    case 'tall':
      body = (
        <g>
          <rect x="53" y="6" width="14" height="10" rx="2" fill={accent} />
          <path d="M54 16 H66 V30 Q76 34 76 46 V108 Q76 112 72 112 H48 Q44 112 44 108 V46 Q44 34 54 30 Z" fill={liquid} />
          <path d="M54 16 H66 V30 Q76 34 76 46 V108 Q76 112 72 112 H48 Q44 112 44 108 V46 Q44 34 54 30 Z" fill={shine} />
          <Label x={46} y={58} w={28} h={34} />
        </g>
      );
      break;
    case 'wine':
      body = (
        <g>
          <rect x="54" y="6" width="12" height="16" rx="2" fill={accent} />
          <path d="M55 22 H65 V44 Q78 52 78 66 V108 Q78 112 74 112 H46 Q42 112 42 108 V66 Q42 52 55 44 Z" fill={liquid} />
          <path d="M55 22 H65 V44 Q78 52 78 66 V108 Q78 112 74 112 H46 Q42 112 42 108 V66 Q42 52 55 44 Z" fill={shine} />
          <Label x={45} y={72} w={30} h={26} />
        </g>
      );
      break;
    case 'round':
      body = (
        <g>
          <rect x="53" y="10" width="14" height="12" rx="3" fill={accent} />
          <path d="M55 22 H65 V34 Q86 42 86 72 Q86 112 60 112 Q34 112 34 72 Q34 42 55 34 Z" fill={liquid} />
          <path d="M55 22 H65 V34 Q86 42 86 72 Q86 112 60 112 Q34 112 34 72 Q34 42 55 34 Z" fill={shine} />
          <Label x={42} y={62} w={36} h={26} rx={8} />
        </g>
      );
      break;
    case 'pet':
      body = (
        <g>
          <rect x="53" y="8" width="14" height="9" rx="2" fill="#2459B8" />
          <path d="M54 17 H66 V26 Q76 30 76 42 V106 Q76 112 70 112 H50 Q44 112 44 106 V42 Q44 30 54 26 Z" fill={liquid} />
          {[48, 62, 96].map((y) => (
            <rect key={y} x="44" y={y} width="32" height="2" fill="#FFFFFF" opacity="0.6" />
          ))}
          <path d="M54 17 H66 V26 Q76 30 76 42 V106 Q76 112 70 112 H50 Q44 112 44 106 V42 Q44 30 54 26 Z" fill={shine} />
          <Label x={44} y={68} w={32} h={24} rx={1} />
        </g>
      );
      break;
    case 'soda':
      body = (
        <g>
          <rect x="54" y="10" width="12" height="8" rx="2" fill={accent} />
          <path d="M55 18 H65 V36 Q74 42 74 54 V106 Q74 112 68 112 H52 Q46 112 46 106 V54 Q46 42 55 36 Z" fill={liquid} />
          <path d="M55 18 H65 V36 Q74 42 74 54 V106 Q74 112 68 112 H52 Q46 112 46 106 V54 Q46 42 55 36 Z" fill={shine} />
          <Label x={46} y={64} w={28} h={26} />
        </g>
      );
      break;
    case 'bag':
      body = (
        <g>
          <path d="M30 22 L90 22 L86 30 Q92 66 86 102 L90 110 L30 110 L34 102 Q28 66 34 30 Z" fill={label} />
          <path d="M30 22 L90 22 L86 30 Q92 66 86 102 L90 110 L30 110 L34 102 Q28 66 34 30 Z" fill={shine} />
          {Array.from({ length: 12 }, (_, i) => (
            <path key={i} d={`M${30 + i * 5} 22 l2.5 4 l2.5 -4`} fill="none" stroke={accent} strokeWidth="1" />
          ))}
          <ellipse cx="60" cy="80" rx="20" ry="12" fill={accent} opacity="0.9" />
          <text x="60" y="56" textAnchor="middle" fontFamily="Inter Variable, Inter, sans-serif" fontWeight="900" fontSize={Math.min(11, 90 / Math.max(4, brandText.length))} fill={labelText}>
            {brandText}
          </text>
          <text x="60" y="83" textAnchor="middle" fontFamily="Inter Variable, Inter, sans-serif" fontWeight="700" fontSize="7" fill={label === '#FFFFFF' ? '#FFFFFF' : '#121821'}>
            {vol || (name.split(' ').slice(-1)[0] ?? '').toUpperCase().slice(0, 10)}
          </text>
        </g>
      );
      break;
    case 'ice':
      body = (
        <g>
          <path d="M34 20 H86 L84 108 Q84 112 80 112 H40 Q36 112 36 108 Z" fill="#EAF5FD" stroke="#7FB8D8" strokeWidth="2" />
          {[
            [44, 56],
            [62, 50],
            [50, 74],
            [68, 72],
            [56, 92],
          ].map(([x, y], i) => (
            <rect key={i} x={x} y={y} width="16" height="16" rx="4" fill="#FFFFFF" stroke="#8FD3FF" strokeWidth="1.5" transform={`rotate(${(i * 17) % 30 - 12} ${x! + 8} ${y! + 8})`} />
          ))}
          <rect x="34" y="20" width="52" height="16" fill="#2459B8" />
          <text x="60" y="31" textAnchor="middle" fontFamily="Inter Variable, Inter, sans-serif" fontWeight="800" fontSize="8" fill="#FFFFFF">
            ICE{vol ? ` · ${vol}` : ''}
          </text>
        </g>
      );
      break;
    default:
      body = (
        <g>
          <rect x="34" y="28" width="52" height="80" rx="6" fill={label} />
          <rect x="34" y="28" width="52" height="80" rx="6" fill={shine} />
          <text x="60" y="70" textAnchor="middle" fontFamily="Inter Variable, Inter, sans-serif" fontWeight="800" fontSize={Math.min(9, 80 / Math.max(4, brandText.length))} fill={labelText}>
            {brandText}
          </text>
        </g>
      );
  }

  return (
    <svg viewBox="0 0 120 120" className={className} role="img" aria-label={name}>
      <defs>
        <linearGradient id={`g${uid}`} x1="0" x2="1">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.55" />
          <stop offset="1" stopColor="#E7EAEE" stopOpacity="0.35" />
        </linearGradient>
        <linearGradient id={`s${uid}`} x1="0" x2="1">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.0" />
          <stop offset="0.18" stopColor="#FFFFFF" stopOpacity="0.32" />
          <stop offset="0.3" stopColor="#FFFFFF" stopOpacity="0.0" />
          <stop offset="0.85" stopColor="#000000" stopOpacity="0.0" />
          <stop offset="1" stopColor="#000000" stopOpacity="0.18" />
        </linearGradient>
      </defs>
      <ellipse cx="60" cy="113" rx="30" ry="4" fill="#000000" opacity="0.10" />
      {body}
    </svg>
  );
}
